/* eslint-disable no-template-curly-in-string */

// Define the Ruby language using legacy stream
import { autocompletion } from '@codemirror/autocomplete';
import { StreamLanguage } from '@codemirror/language';
import { html } from '@codemirror/lang-html';
import {
  javascript,
  javascriptLanguage,
  localCompletionSource,
} from '@codemirror/lang-javascript';
import { json } from '@codemirror/lang-json';
import { ruby } from '@codemirror/legacy-modes/mode/ruby';
import { parseMixed } from '@lezer/common';
import { jsTemplate, javascriptInnerLanguage } from '../codemirror/js_template';
import { rubyTemplate } from '../codemirror/ruby_template';
import {
  buildAutocompletions,
  buildJsTemplateAutocompletions,
  buildRubyTemplateAutocompletions,
} from './autocomplete';

// Define jsTemplate language support since it's used in multiple different
// language and type combinations
const jsTemplateSupport = jsTemplate();

// Define html language support
const htmlSupport = html();

// Define the Ruby language using legacy stream
const rubyLanguage = StreamLanguage.define(ruby);

// Define rubyTemplate language support
const rubyTemplateSupport = rubyTemplate();

/**
 * Builds the appropriate extensions for the CodeMirror editor to parse the
 * content in the provided language.
 *
 * @param {string} language Language that the editor should parse
 * @param {object[]} bindings List of bindings to use for completions
 * @param {boolean} disabled Is the editor disabled
 * @returns {array} List of extensions for the CodeMirror editor
 */
export const buildLanguageExtensions = (language, bindings, disabled) => {
  switch (language) {
    case 'js-template':
    case 'text':
      return [
        jsTemplateSupport,
        // Completions for outside interpolations (and inside empty ones)
        !disabled &&
          jsTemplateSupport.language.data.of({
            autocomplete: buildJsTemplateAutocompletions(bindings),
          }),
        // Completions for inside interpolations
        !disabled &&
          javascriptInnerLanguage.data.of({
            autocomplete: buildAutocompletions(bindings),
          }),
        // Clear any completions if the editor is disabled
        disabled && autocompletion({ override: [] }),
      ].filter(Boolean);
    case 'js-expression':
      return [
        javascriptLanguage.configure({ top: 'SingleExpression' }),
        // Completions
        !disabled &&
          javascriptLanguage.data.of({
            autocomplete: buildAutocompletions(bindings),
          }),
        // Clear any completions if the editor is disabled
        disabled && autocompletion({ override: [] }),
      ].filter(Boolean);
    case 'js':
      return [
        javascript(),
        // Override default completions so we can include only useful ones.
        // Add custom completions, local completions, and snippets.
        autocompletion({
          // Only include completions if the editor is not disabled
          override: !disabled
            ? [
                // Add custom binding completions, as well as the predefined
                // javascript snippets
                buildAutocompletions(
                  JAVASCRIPT_SNIPPETS.concat(bindings || []),
                ),
                // Local completions (dynamic variables defined in the code)
                localCompletionSource,
              ]
            : [],
        }),
      ].filter(Boolean);
    case 'json':
      return [json()];
    case 'html':
      return [
        // Parse Text nodes inside the HTML parser using the jsTemplate language
        // so we can insert interpolations into the html
        htmlSupport.language.configure({
          wrap: parseMixed(node => {
            return node.name === 'Text'
              ? { parser: jsTemplateSupport.language.parser }
              : null;
          }),
        }),
        // Include the default HTML support
        htmlSupport.support,
        // Completions for outside interpolations (and inside empty ones)
        !disabled &&
          jsTemplateSupport.language.data.of({
            autocomplete: buildJsTemplateAutocompletions(bindings),
          }),
        // Completions for inside interpolations
        !disabled &&
          javascriptInnerLanguage.data.of({
            autocomplete: buildAutocompletions(bindings),
          }),
        // Clear any completions if the editor is disabled
        disabled && autocompletion({ override: [] }),
      ].filter(Boolean);
    case 'ruby-template':
    case 'erb':
      return [
        rubyTemplateSupport,
        // Completions for inside and outside of interpolations because the
        // Ruby legacy mode doesn't apply its completions when used as an inner
        // parser using parseMixed. The completions will need to check if
        // they're inside or outside an interpolation.
        !disabled &&
          rubyTemplateSupport.language.data.of({
            autocomplete: buildRubyTemplateAutocompletions(bindings),
          }),

        // Clear any completions if the editor is disabled
        disabled && autocompletion({ override: [] }),
      ].filter(Boolean);
    case 'ruby-expression':
    case 'ruby':
      return [
        rubyLanguage,
        // Override the completions so we don't get basic ruby syntax inside
        // an expression.
        autocompletion({
          // Only include completions if the editor is not disabled
          override: !disabled ? [buildAutocompletions(bindings)] : [],
        }),
      ];
    default:
      return [];
  }
};

/**
 * Returns the pretty version of the language name
 *
 * @param {string} language
 * @returns {string}
 */
export const getLanguageName = language => {
  switch (language) {
    case 'js':
    case 'js-expression':
    case 'js-template':
      return 'JavaScript';
    case 'json':
      return 'JSON';
    case 'html':
      return 'HTML';
    case 'ruby':
    case 'ruby-expression':
    case 'ruby-template':
    case 'erb':
      return 'Ruby';
    default:
      return 'Plain Text';
  }
};

export const getLanguageSyntax = language => {
  switch (language) {
    case 'js-template':
    case 'ruby-template':
    case 'erb':
      return 'Template';
    case 'js-expression':
    case 'ruby-expression':
      return 'Expression';
    default:
      return undefined;
  }
};

export const getLanguageHelpText = language => {
  switch (language) {
    case 'js-expression':
    case 'ruby-expression':
      return (
        <span>An expression is code that evaluates to a single value.</span>
      );
    case 'js-template':
      return (
        <span>
          A JavaScript template is text that may contain embedded expressions
          using the syntax: <code>{'${expression}'}</code>
        </span>
      );
    case 'ruby-template':
    case 'erb':
      return (
        <span>
          A Ruby template is text that may contain embedded expressions using
          the syntax: <code>{'<%= expression %>}'}</code>
        </span>
      );
    default:
      return undefined;
  }
};

const JAVASCRIPT_SNIPPETS = [
  {
    label: 'function',
    snippet: 'function ${name}(${params}) {\n\t${}\n}',
    detail: 'definition',
  },
  {
    snippet:
      'for (let ${index} = 0; ${index} < ${bound}; ${index}++) {\n\t${}\n}',
    label: 'for',
    detail: 'loop',
  },
  {
    label: 'for',
    snippet: 'for (let ${name} of ${collection}) {\n\t${}\n}',
    detail: 'of loop',
  },
  {
    label: 'do',
    snippet: 'do {\n\t${}\n} while (${})',
    detail: 'loop',
  },
  {
    label: 'while',
    snippet: 'while (${}) {\n\t${}\n}',
    detail: 'loop',
  },
  {
    label: 'try',
    snippet: 'try {\n\t${}\n} catch (${error}) {\n\t${}\n}',
    detail: '/ catch block',
  },
  {
    label: 'if',
    snippet: 'if (${}) {\n\t${}\n}',
    detail: 'block',
  },
  {
    label: 'if',
    snippet: 'if (${}) {\n\t${}\n} else {\n\t${}\n}',
    detail: '/ else block',
  },
].map(s => ({
  ...s,
  // Use a higher rank than default (1e9) so this section will always be last
  section: { name: 'Javascript Snippets', rank: 1e9 + 1000 },
}));
